import { ESdChangeReason, EUserAccessPart } from "@kuika/kuika-cl-model";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { withHistory } from "../../../../shared/hoc/with-history";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { isBranchingUIEnabled } from "../../branching/branching-feature-gate";
import { UserMenuButton } from "../../left-menu/user-menu-button";
import { DevTools } from "../dev-tools";
import { AiChatButton } from "./ai-chat-button";
import { BranchSelectorButton } from "./branch-selector/branch-selector-button";
import { CollabPresence } from "./collab-presence";
import { IssuesButton } from "./issues/issues-button";
import { RunButton } from "./run-button/run-button";
import { WorkflowEngineStatus } from "./run-button/workflow-engine-status";
import { Save } from "./save";
import "./toolbar-right.scss";

const ACTION_TRANSITION = { duration: 0.18, ease: "easeOut" } as const;

interface IAnimatedActionProps {
  children: React.ReactNode;
}

/**
 * Fades a presence-varying control (Save, report-designer only) in/out on tab/access change
 * instead of snapping the DOM. Honors reduced-motion (drops the horizontal slide, keeps the fade).
 */
const AnimatedAction = ({ children }: IAnimatedActionProps): React.ReactElement => {
  const shouldReduceMotion: boolean | null = useReducedMotion();
  const offset: number = shouldReduceMotion ? 0 : 8;
  return (
    <motion.div
      className="kuika-toolbar-right__action"
      initial={{ opacity: 0, x: offset }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: offset }}
      transition={ACTION_TRANSITION}
    >
      {children}
    </motion.div>
  );
};

declare let window: Window & {
  enableKuikaDevTools: (() => void) | undefined;
  disableKuikaDevTools: (() => void) | undefined;
};

export interface IToolbarRightState {
  isDevToolsEnabled: boolean;
}

export interface IToolbarRightProps {}

/**
 * Right region of the top bar. Controls are fixed-size and never grow on hover, with one
 * deliberate exception: after a preview the Preview button widens for 3s to say how it ended
 * ("Ready", "Failed", "Busy") and shrinks back to its icon. The row is right-aligned, so that
 * growth pushes only leftward and the controls to its right never move.
 */
class ToolbarRight extends PureSdComponent<IToolbarRightProps & IWithSDProps & IWithContextProp, IToolbarRightState> {
  constructor(props: IToolbarRightProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isDevToolsEnabled: false
    };
  }

  componentDidMount() {
    window.enableKuikaDevTools = () => {
      this.setState({ isDevToolsEnabled: true });
      localStorage.setItem("ENABLE_KUIKA_DEV_TOOLS", "true");
    };

    window.disableKuikaDevTools = () => {
      this.setState({ isDevToolsEnabled: false });
      localStorage.setItem("ENABLE_KUIKA_DEV_TOOLS", "false");
    };

    if (localStorage.getItem("ENABLE_KUIKA_DEV_TOOLS") === "true") {
      this.setState({ isDevToolsEnabled: true });
    }
  }

  componentWillUnmount() {
    window.enableKuikaDevTools = undefined;
    window.disableKuikaDevTools = undefined;
  }

  render() {
    const hasUserAccessPreview = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.PreviewProject,
      this.props.sd.project?.projectAccess
    );
    const hasUserAccessSaveScreen = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.SaveScreen,
      this.props.sd.project?.projectAccess
    );

    return (
      <div className="kuika-toolbar-right">
        {this.state.isDevToolsEnabled && <DevTools />}
        <CollabPresence />
        {/* Save is report-designer-only; fade it in/out on change rather than snapping. */}
        <AnimatePresence initial={false}>
          {hasUserAccessSaveScreen && (
            <AnimatedAction key="save">
              <Save />
            </AnimatedAction>
          )}
        </AnimatePresence>
        {/* AI sits immediately left of the preview/run group — it replaced the floating FAB that
            used to hover over the canvas. */}
        <AiChatButton />
        {hasUserAccessPreview && <WorkflowEngineStatus />}
        {/* Preview and Issues are one group: what a preview finds (build and codegen failures
            join the design-time validation count) lands in the control right beside it. */}
        <div className="kuika-toolbar-right__run-group">
          {hasUserAccessPreview && <RunButton />}
          <IssuesButton />
        </div>
        {/* Version control sits with the project actions, just before the avatar. With it
            ACTIVATED this is the branch pill; before activation the selector renders the compact
            "activate" chip, which opens the activation confirmation. */}
        {isBranchingUIEnabled() && <BranchSelectorButton />}
        {/* No divider before the avatar — the Figma redesign lets the 8px rhythm carry the row. */}
        <UserMenuButton />
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const toolbarRight = withContext<IToolbarRightProps>(
  withSD<IToolbarRightProps>(withHistory<IToolbarRightProps>(ToolbarRight))
);
export { toolbarRight as ToolbarRight };
